import { i18n } from '@apps/main/modules/localization/localizationSetup';
import type {
  FerryIngredient,
  FlightIngredient,
  HotelIngredient,
  RecipeMainIngredient,
} from '@apps/main/modules/recipe/ingredients';
import {
  isFlightIngredient,
  isHotelIngredient,
} from '@apps/main/modules/recipe/ingredients';
import type { OwnStayIngredient } from '@apps/main/modules/recipe/ingredients/ownStayIngredient';
import type { OwnTransportIngredient } from '@apps/main/modules/recipe/ingredients/ownTransportIngredient';
import type { StayIngredient } from '@apps/main/modules/recipe/ingredients/typeGuards';
import {
  isOwnStayIngredient,
  isOwnTransportIngredient,
  isStayIngredient,
  isTransportIngredient,
} from '@apps/main/modules/recipe/ingredients/typeGuards';
import {
  getActiveFilters,
  getHotelFilterFromIngredient,
} from '@apps/main/new-desktop/modules/hotelFilters';
import { SuggestionType } from '@apps/main/new-desktop/modules/suggestionTypeSelector';

import { isStena } from '@libs/cookie';
import type { CarrierName, IngredientId } from '@libs/trip-planner-interfaces';
import {
  AvailabilityOrder,
  SearchTermType,
  TransportType,
} from '@libs/trip-planner-interfaces';

import type {
  ItineraryLoaderSegment,
  ItineraryNoTransportLoaderSegment,
  ItineraryOwnTransportLoaderSegment,
  ItinerarySimpleTransportLoaderSegment,
  ItineraryStenaUpsellNoTransportSegment,
  ItineraryUpsellNoTransportSegment,
  LocationSegment,
} from '../models';
import { ItinerarySegmentType, ProductCardTopBarType } from '../models';

import {
  getCityText,
  getCountryText,
  getLocationSegment,
} from './tripOverviewSegments/tripOverviewLocationSegmentsLogic';
import { getItinerarySegmentTypeFromTransportType } from './tripOverviewSegments/tripOverviewSimpleTransportSegmentsLogic';
import {
  getFerryFilterPills,
  getFlightFilterPills,
} from './tripOverviewSegments/tripOverviewTransportFiltersPillsLogic';

interface GetHotelLoaderSegmentsParams {
  recipe: RecipeMainIngredient[];
  ingredient: HotelIngredient;
  ingredientIndex: number;
  isStena: boolean;
  isSmallDevice: boolean;
  getFlightAllCarriers: (ingredientId: IngredientId) => CarrierName[];
  getFerryAllCarriers: (ingredientId: IngredientId) => CarrierName[];
  currency: string;
}

interface GetOwnTransportLoaderSegmentsParams {
  recipe: RecipeMainIngredient[];
  ingredient: OwnTransportIngredient;
  ingredientIndex: number;
  isStena: boolean;
}

const { t } = i18n.global;

/* eslint-disable @typescript-eslint/no-unused-vars */

export function getTripItineraryLoaderSegments(
  recipe: RecipeMainIngredient[],
  isGeolocationAvailable: boolean,
  isSmallDevice: boolean,
  getFlightAllCarriers: (ingredientId: IngredientId) => CarrierName[],
  getFerryAllCarriers: (ingredientId: IngredientId) => CarrierName[],
  currency: string
): ItineraryLoaderSegment[] {
  const loaderSegments: ItineraryLoaderSegment[] = [];
  const firstIngredient = recipe[0];

  if (
    isGeolocationAvailable &&
    firstIngredient &&
    isOwnTransportIngredient(firstIngredient) &&
    firstIngredient.ownTransportType !== TransportType.None
  ) {
    loaderSegments.push({
      segmentType: ItinerarySegmentType.StartLocation,
      location: { name: t('planner.your_position') },
    });
  }

  const recipeLoaders = recipe.flatMap(
    (ingredient: RecipeMainIngredient, index: number) => {
      const ingredientLoaders: ItineraryLoaderSegment[] = [];

      if (isHotelIngredient(ingredient)) {
        const hotelLoadersSementParams: GetHotelLoaderSegmentsParams = {
          recipe,
          ingredient,
          ingredientIndex: index,
          isStena: isStena(),
          isSmallDevice,
          getFlightAllCarriers,
          getFerryAllCarriers,
          currency,
        };

        const hotelLoaders = getHotelLoaderSegments(hotelLoadersSementParams);
        ingredientLoaders.push(...hotelLoaders);
      }

      if (isOwnTransportIngredient(ingredient)) {
        const ownTransportLoaderSementParams: GetOwnTransportLoaderSegmentsParams =
          {
            recipe,
            ingredient,
            ingredientIndex: index,
            isStena: isStena(),
          };

        const ownTransportLoader = getOwnTransportLoaderSegment(
          ownTransportLoaderSementParams
        );
        if (ownTransportLoader) {
          ingredientLoaders.push(ownTransportLoader);
        }
      }

      if (isOwnStayIngredient(ingredient)) {
        const ownStayLoaders = getOwnStayLoaderSegments(
          recipe,
          ingredient,
          index,
          getFlightAllCarriers,
          getFerryAllCarriers,
          currency
        );
        ingredientLoaders.push(...ownStayLoaders);
      }

      const isLastIngredientOnlyBookableTransport =
        isTransportIngredient(ingredient) && index === recipe.length - 1;

      if (isLastIngredientOnlyBookableTransport) {
        const productCardTopBarType = getTransportProductTopBarType(ingredient);

        let suggestionType: SuggestionType | undefined = undefined;
        if (productCardTopBarType !== ProductCardTopBarType.Selected) {
          suggestionType =
            productCardTopBarType === ProductCardTopBarType.Suggestion
              ? SuggestionType.MostPopular
              : SuggestionType.Cheapest;
        }

        if (isFlightIngredient(ingredient)) {
          const filtersPills = getFlightFilterPills(
            ingredient.filters,
            getFlightAllCarriers(ingredient.ingredientId),
            currency
          );

          ingredientLoaders.push({
            segmentType: ItinerarySegmentType.Flight,
            ingredientId: ingredient.ingredientId,
            isLastSegment: true,
            barType: productCardTopBarType,
            filtersPills,
            suggestionType,
          });
        } else {
          const filtersPills = getFerryFilterPills(
            ingredient.filters,
            getFerryAllCarriers(ingredient.ingredientId)
          );

          ingredientLoaders.push({
            segmentType: ItinerarySegmentType.Ferry,
            ingredientId: ingredient.ingredientId,
            isLastSegment: true,
            barType: productCardTopBarType,
            filtersPills,
            suggestionType,
          });
        }
      }

      return ingredientLoaders;
    }
  );

  loaderSegments.push(...recipeLoaders);

  const lastRecipeIngredient = recipe[recipe.length - 1];

  if (
    lastRecipeIngredient &&
    isOwnTransportIngredient(lastRecipeIngredient) &&
    lastRecipeIngredient.ownTransportType !== TransportType.None
  ) {
    loaderSegments.push({
      segmentType: ItinerarySegmentType.EndLocation,
      location: { name: t('planner.your_position') },
    });
  }

  return loaderSegments;
}

function getTransportProductTopBarType(
  ingerdient: FlightIngredient | FerryIngredient
): ProductCardTopBarType | undefined {
  if (ingerdient.transportIds?.length) {
    return undefined;
  }

  if (ingerdient.availabilityOrder === AvailabilityOrder.Popular) {
    return ProductCardTopBarType.Suggestion;
  } else {
    return ProductCardTopBarType.Cheapest;
  }
}

function getHotelProductTopBarType(
  ingerdient: HotelIngredient
): ProductCardTopBarType {
  if (ingerdient.heartedHotels?.length) {
    return ProductCardTopBarType.Selected;
  }

  return ingerdient.availabilityOrder === AvailabilityOrder.Popular
    ? ProductCardTopBarType.Suggestion
    : ProductCardTopBarType.Cheapest;
}

function getHotelLoaderSegments(
  params: GetHotelLoaderSegmentsParams
): ItineraryLoaderSegment[] {
  const ingredientLoaders: ItineraryLoaderSegment[] = [];

  const previousIngredient: RecipeMainIngredient | undefined =
    params.recipe[params.ingredientIndex - 1];

  ingredientLoaders.push(
    ...getBookableTransportLoaderSegments(
      previousIngredient,
      params.getFlightAllCarriers,
      params.getFerryAllCarriers,
      params.currency
    )
  );

  ingredientLoaders.push({
    segmentType: ItinerarySegmentType.DateLocation,
    location: getHotelDateLocationLoaderLocationSegment(params.ingredient),
    numberOfNights: params.ingredient.numberOfNights,
    ingredientId: params.ingredient.ingredientId,
  });

  ingredientLoaders.push({
    segmentType: ItinerarySegmentType.Accommodation,
    barType: getHotelProductTopBarType(params.ingredient),
    ingredientId: params.ingredient.ingredientId,
    ingredientIndex: params.ingredientIndex,
    filters: params.ingredient.filters
      ? getActiveFilters(
          getHotelFilterFromIngredient(params.ingredient.filters)
        )
      : [],
  });

  params.ingredient.subIngredients.forEach((subIngredient) =>
    ingredientLoaders.push({
      segmentType: ItinerarySegmentType.Activity,
      barType: undefined,
      ingredientId: subIngredient.ingredientId,
      ingredientIndex: params.ingredientIndex,
    })
  );

  ingredientLoaders.push({
    segmentType: ItinerarySegmentType.Upsell,
    ingredientId: params.ingredient.ingredientId,
  });

  return ingredientLoaders;
}

function getOwnTransportLoaderSegment(
  params: GetOwnTransportLoaderSegmentsParams
): ItineraryOwnTransportLoaderSegment | undefined {
  const previousIngredient: RecipeMainIngredient | undefined =
    params.recipe[params.ingredientIndex - 1];
  const nextIngredient: RecipeMainIngredient | undefined =
    params.recipe[params.ingredientIndex + 1];
  if (
    params.ingredientIndex === 0 &&
    params.ingredient.ownTransportType === TransportType.None
  ) {
    if (params.isStena) {
      return getStenaUpsellLoaderSegments(params.ingredient);
    } else if (nextIngredient && isHotelIngredient(nextIngredient)) {
      return getTransportUpsellLoaderSegments(
        nextIngredient,
        params.ingredient
      );
    }
  } else {
    return getSimpleTransportLoaderSegments(
      params.ingredient,
      previousIngredient,
      params.recipe
    );
  }
}

function getOwnStayLoaderSegments(
  recipe: RecipeMainIngredient[],
  ingredient: OwnStayIngredient,
  ingredientIndex: number,
  getFlightAllCarriers: (ingredientId: IngredientId) => CarrierName[],
  getFerryAllCarriers: (ingredientId: IngredientId) => CarrierName[],
  currency: string
): ItineraryLoaderSegment[] {
  const ingredientLoaders: ItineraryLoaderSegment[] = [];

  const previousIngredient: RecipeMainIngredient | undefined =
    recipe[ingredientIndex - 1];

  ingredientLoaders.push(
    ...getBookableTransportLoaderSegments(
      previousIngredient,
      getFlightAllCarriers,
      getFerryAllCarriers,
      currency
    )
  );

  ingredientLoaders.push({
    segmentType: ItinerarySegmentType.DateLocation,
    location: getLocationSegment(ingredient.displayText),
    numberOfNights: ingredient.numberOfNights,
    ingredientId: ingredient.ingredientId,
  });

  if (ingredient.numberOfNights !== 0) {
    ingredientLoaders.push({
      segmentType: ItinerarySegmentType.OwnStay,
      addAccommodationMessage: `${t(
        'planner.no_availability.actions.explore_hotels_selection_in'
      )} ${getCityText(ingredient.displayText)}`,
      context: {
        ingredientId: ingredient.ingredientId,
        ingredientIndex,
      },
    });
  }

  ingredient.subIngredients.forEach((subIngredient) =>
    ingredientLoaders.push({
      segmentType: ItinerarySegmentType.Activity,
      barType: undefined,
      ingredientId: subIngredient.ingredientId,
      ingredientIndex,
    })
  );

  ingredientLoaders.push({
    segmentType: ItinerarySegmentType.Upsell,
    ingredientId: ingredient.ingredientId,
  });

  return ingredientLoaders;
}

function getBookableTransportLoaderSegments(
  previousIngredient: RecipeMainIngredient | undefined,
  getFlightAllCarriers: (ingredientId: IngredientId) => CarrierName[],
  getFerryAllCarriers: (ingredientId: IngredientId) => CarrierName[],
  currency: string
): ItineraryLoaderSegment[] {
  const bookableTransportLoaderSegments: ItineraryLoaderSegment[] = [];

  const isPreviousIngredientBookableTransport =
    previousIngredient && isTransportIngredient(previousIngredient);

  if (isPreviousIngredientBookableTransport) {
    const productCardTopBarType =
      getTransportProductTopBarType(previousIngredient);

    let suggestionType: SuggestionType | undefined = undefined;
    if (productCardTopBarType !== ProductCardTopBarType.Selected) {
      suggestionType =
        productCardTopBarType === ProductCardTopBarType.Suggestion
          ? SuggestionType.MostPopular
          : SuggestionType.Cheapest;
    }

    if (isFlightIngredient(previousIngredient)) {
      const filtersPills = getFlightFilterPills(
        previousIngredient.filters,
        getFlightAllCarriers(previousIngredient.ingredientId),
        currency
      );

      bookableTransportLoaderSegments.push({
        segmentType: ItinerarySegmentType.Flight,
        ingredientId: previousIngredient.ingredientId,
        barType: productCardTopBarType,
        filtersPills,
        suggestionType,
      });
    } else {
      const filtersPills = getFerryFilterPills(
        previousIngredient.filters,
        getFerryAllCarriers(previousIngredient.ingredientId)
      );

      bookableTransportLoaderSegments.push({
        segmentType: ItinerarySegmentType.Ferry,
        ingredientId: previousIngredient.ingredientId,
        barType: productCardTopBarType,
        filtersPills,
        suggestionType,
      });
    }
  }

  return bookableTransportLoaderSegments;
}

function getSimpleTransportLoaderSegments(
  ingredient: OwnTransportIngredient,
  previousIngredient: RecipeMainIngredient | undefined,
  recipe: RecipeMainIngredient[]
):
  | ItinerarySimpleTransportLoaderSegment
  | ItineraryNoTransportLoaderSegment
  | undefined {
  const isPreviousIngredientStay =
    previousIngredient && isStayIngredient(previousIngredient);

  const isLastSegment = ingredient.ingredientId === recipe.at(-1)?.ingredientId;
  if (isPreviousIngredientStay || !previousIngredient) {
    if (ingredient.ownTransportType !== TransportType.None) {
      return {
        segmentType: getItinerarySegmentTypeFromTransportType(
          ingredient.ownTransportType
        ),
        ingredientId: ingredient.ingredientId,
        isLastSegment: isLastSegment ? isLastSegment : undefined,
      };
    } else {
      return {
        segmentType: ItinerarySegmentType.NoTransport,
        ingredientId: ingredient.ingredientId,
        isReturnTransport: isLastSegment,
      };
    }
  }
}

function getHotelDateLocationLoaderLocationSegment(
  ingredient: HotelIngredient
): LocationSegment {
  const polygonText =
    ingredient.commonPolygon?.name ||
    ingredient.nearbyPolygon?.name ||
    ingredient.displayText ||
    t('planner.your_destination');

  return getLocationSegment(polygonText);
}

function getTransportUpsellLoaderSegments(
  ingredient: StayIngredient,
  ownTransportIngredient: OwnTransportIngredient
): ItineraryUpsellNoTransportSegment {
  return {
    segmentType: ItinerarySegmentType.UpsellNoTransport,
    ingredientId: ownTransportIngredient.ingredientId,
    destination: getCountryText(
      ingredient.displayText,
      ingredient.searchTerm.type === SearchTermType.FreeText
        ? ingredient.searchTerm.freeText
        : undefined
    ),
  };
}

function getStenaUpsellLoaderSegments(
  ingredient: StayIngredient | OwnTransportIngredient
): ItineraryStenaUpsellNoTransportSegment {
  return {
    segmentType: ItinerarySegmentType.StenaUpsellNoTransport,
    ingredientId: ingredient.ingredientId,
  };
}
